import React from 'react'
import type { FieldError as HookFieldError } from 'react-hook-form'

import ColorField, { Props as ColorFieldProps } from '@/components/ColorField'
import FieldError from '@/components/livecontact/FieldError'
import LcIcon from '@/components/livecontact/icons'
import { LABEL_CLASS } from '@/components/livecontact/fieldStyles'

import { cn } from '@/lib/utils'
import { onColor } from '@/lib/color/contrast'

export type ColorPreset = {
  name: string
  primary: string
  secondary?: string
}

type ColorValue = string | { primary: string; secondary?: string }

type DualColorFieldProps = {
  label?: string
  value: ColorValue
  onChange: (value: ColorValue) => void
  presets?: ColorPreset[]
  showPresets?: boolean
  compact?: boolean
  colorFieldProps?: Omit<ColorFieldProps, 'value' | 'onChange' | 'label'>
  error?: HookFieldError
}

/**
 * Figma "Primary Color": 44px preset circles with names in a 4-column grid, then the swatch chip(s).
 * Selected preset = ink ring + check that pops in; hover is a ring change only (no lift).
 */
const DualColorField: React.FC<DualColorFieldProps> = ({
  label,
  value,
  onChange,
  presets = [],
  showPresets = false,
  compact = false,
  colorFieldProps,
  error,
}) => {
  const isString = typeof value === 'string'
  const primaryColor = isString ? value : value?.primary
  const secondaryColor = isString ? undefined : value?.secondary

  const handlePresetClick = (preset: ColorPreset) => {
    if (isString) {
      onChange(preset.primary)
    } else {
      onChange({ primary: preset.primary, secondary: preset.secondary || preset.primary })
    }
  }

  const handlePrimaryChange = (color: string) =>
    isString ? onChange(color) : onChange({ primary: color, secondary: secondaryColor })

  const handleSecondaryChange = (color: string) =>
    isString ? onChange(color) : onChange({ primary: primaryColor, secondary: color })

  // Presets stay available on every plan; only the custom swatch is gated (via colorFieldProps).
  return (
    <div className={cn('flex flex-col', compact ? 'gap-3' : 'gap-5')}>
      {label && <p className={cn(LABEL_CLASS, 'mb-0')}>{label}</p>}

      {showPresets && presets.length > 0 && (
        <div className="flex flex-col gap-3">
          <span className={cn(LABEL_CLASS, 'mb-0')}>Presets</span>
          <div
            className="grid grid-cols-5 gap-x-2 gap-y-4 sm:grid-cols-4"
            role="group"
            aria-label="Colour presets"
          >
            {presets.map((preset) => {
              const selected = primaryColor?.toLowerCase() === preset.primary.toLowerCase()
              return (
                <button
                  key={preset.name}
                  type="button"
                  onClick={() => handlePresetClick(preset)}
                  aria-pressed={selected}
                  title={preset.name}
                  className="group flex flex-col items-center gap-2 rounded-[10px] py-1 outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                >
                  <span
                    className={cn(
                      'relative inline-flex size-10 items-center justify-center rounded-full',
                      'shadow-[inset_0_0_0_1px_rgba(0,0,0,0.06)] transition-[transform,box-shadow] duration-[160ms] ease-out-strong',
                      'group-active:scale-[0.96]',
                      selected
                        ? 'ring-2 ring-ink ring-offset-2 ring-offset-surface'
                        : 'group-hover:ring-2 group-hover:ring-line-2 group-hover:ring-offset-2 group-hover:ring-offset-surface'
                    )}
                    style={{ backgroundColor: preset.primary }}
                  >
                    {selected && (
                      <LcIcon
                        name="check"
                        size={16}
                        strokeWidth={2.5}
                        className="animate-lc-pop"
                        style={{ color: onColor(preset.primary) }}
                      />
                    )}
                    {preset.secondary && (
                      <span
                        className="absolute -bottom-0.5 -right-0.5 size-4 rounded-full ring-2 ring-surface"
                        style={{ backgroundColor: preset.secondary }}
                      />
                    )}
                  </span>
                  <span
                    className={cn(
                      'w-full text-center text-[11px] font-medium leading-[1.25] tracking-[-0.2px] transition-colors duration-[120ms]',
                      selected ? 'text-ink' : 'text-ink-3 group-hover:text-ink-2'
                    )}
                  >
                    {preset.name}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      <div
        className={cn(
          secondaryColor !== undefined
            ? 'grid grid-cols-2 gap-4 sm:grid-cols-1'
            : 'flex flex-col gap-4'
        )}
      >
        <ColorField
          label="Primary"
          value={primaryColor}
          onChange={handlePrimaryChange}
          {...colorFieldProps}
        />
        {secondaryColor !== undefined && (
          <ColorField
            label="Secondary"
            value={secondaryColor}
            onChange={handleSecondaryChange}
            {...colorFieldProps}
          />
        )}
      </div>
      <FieldError message={error?.message} className="mt-0" />
    </div>
  )
}

export default DualColorField
